import type React from 'react';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  BandcampSection,
  Footer,
  Release,
  SoundCloudSection,
  TelegramSection,
  Welcome,
  YouTubeSection,
} from '../components';
import { joevData, releaseData, soundCloudData } from '../data';
import type { Release as ReleaseType } from '../types';

export const HomePage: React.FC = () => {
  const navigate = useNavigate();
  const [releases] = useState<ReleaseType[]>(releaseData);
  const [logoClicked, setLogoClicked] = useState(true);

  // Restore scroll position to target release if returning from detail overlay and clean up anchor
  useEffect(() => {
    try {
      const hash = window.location.hash.replace('#', '');
      const stored = sessionStorage.getItem('joev_release_scroll_target');
      const targetId = hash || stored;

      if (targetId) {
        sessionStorage.removeItem('joev_release_scroll_target');
        const timer = setTimeout(() => {
          const el =
            document.getElementById(targetId) || document.getElementById(`release-${targetId}`);
          if (el) {
            el.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }
          if (window.location.hash) {
            window.history.replaceState(
              null,
              '',
              window.location.pathname + window.location.search
            );
          }
        }, 120);

        const handleScrollCleanup = () => {
          if (window.location.hash) {
            window.history.replaceState(
              null,
              '',
              window.location.pathname + window.location.search
            );
          }
          window.removeEventListener('scroll', handleScrollCleanup);
        };

        window.addEventListener('scroll', handleScrollCleanup, { passive: true });

        return () => {
          clearTimeout(timer);
          window.removeEventListener('scroll', handleScrollCleanup);
        };
      }
    } catch {
      // Ignore sessionStorage / SSR errors
    }
  }, []);

  const handleLogoClicked = () => {
    setLogoClicked(true);
    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
  };

  const handleDownClicked = () => {
    const firstRelease = document.getElementById('releases');
    if (firstRelease) {
      firstRelease.scrollIntoView({ behavior: 'smooth' });
    } else {
      window.scrollTo({
        top: window.innerHeight,
        behavior: 'smooth',
      });
    }
  };

  const handleAnimationEnd = () => {
    setLogoClicked(false);
  };

  const handleReleaseClick = (release: ReleaseType) => {
    try {
      sessionStorage.setItem('joev_release_scroll_target', `release-${release.detail_link}`);
    } catch {
      // Ignore storage errors
    }
    navigate(`/details/${release.detail_link}/`);
  };

  return (
    <>
      <Welcome
        handleAnimationEnd={handleAnimationEnd}
        handleLogoClicked={handleLogoClicked}
        logoClicked={logoClicked}
        handleDownClicked={handleDownClicked}
        releaseAmount={releases.length + 1}
        joevData={joevData}
        bgColor=""
      />

      <div id="releases">
        {releases.map((release) => (
          <Release
            key={release.detail_link}
            {...release}
            handleClick={() => handleReleaseClick(release)}
          />
        ))}
      </div>

      <SoundCloudSection tracks={soundCloudData} />
      <BandcampSection />
      <YouTubeSection />
      <TelegramSection />
      <Footer />
    </>
  );
};

export default HomePage;
